import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Badge } from '@/components/atoms/Badge';
import { Card } from '@/components/molecules/Card';
import { DataTable } from '@/components/organisms/DataTable';
import type { DataTableColumn } from '@/components/organisms/DataTable';
import { useToast } from '@/context/ToastContext';
import { ROUTES } from '@/constants/routes';
import {
  listFeedbackCampaigns,
  createFeedbackCampaign,
  updateFeedbackCampaign,
  deleteFeedbackCampaign,
  type FeedbackCampaign,
} from '@/services/api/feedbackAdmin.api';
import styles from './FeedbackListPage.module.scss';

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}
function msToInput(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
const startOfDayMs = (dateStr: string) => new Date(`${dateStr}T00:00:00`).getTime();
const endOfDayMs = (dateStr: string) => new Date(`${dateStr}T23:59:59.999`).getTime();

type CampaignStatus = 'activa' | 'cerrada' | 'programada';
/** Estado temporal respecto a hoy: activa (en curso), cerrada (pasada) o programada (futura). */
function campaignStatus(c: FeedbackCampaign): CampaignStatus {
  if (c.active) return 'activa';
  return c.endDate < Date.now() ? 'cerrada' : 'programada';
}
const STATUS_BADGE: Record<CampaignStatus, { label: string; variant: 'success' | 'default' | 'info' }> = {
  activa: { label: 'Activa', variant: 'success' },
  cerrada: { label: 'Cerrada', variant: 'default' },
  programada: { label: 'Programada', variant: 'info' },
};

export function FeedbackListPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [campaigns, setCampaigns] = useState<FeedbackCampaign[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Formulario de campaña (crear o editar).
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formStart, setFormStart] = useState('');
  const [formEnd, setFormEnd] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setIsLoading(true);
    listFeedbackCampaigns()
      .then((list) => {
        setCampaigns(list);
        setError(null);
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Error cargando las campañas')
      )
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setFormName('');
    setFormStart('');
    setFormEnd('');
  };

  const startCreate = () => {
    setEditingId(null);
    setFormName('');
    setFormStart('');
    setFormEnd('');
    setFormOpen(true);
  };

  const startEdit = (c: FeedbackCampaign) => {
    setEditingId(c.id);
    setFormName(c.name);
    setFormStart(msToInput(c.startDate));
    setFormEnd(msToInput(c.endDate));
    setFormOpen(true);
  };

  const submitCampaign = async () => {
    if (!formName.trim() || !formStart || !formEnd) {
      toast({ description: 'Completa nombre y fechas.', variant: 'default' });
      return;
    }
    const payload = {
      name: formName.trim(),
      startDate: startOfDayMs(formStart),
      endDate: endOfDayMs(formEnd),
    };
    setBusy(true);
    try {
      if (editingId) {
        await updateFeedbackCampaign(editingId, payload);
        toast({ title: 'Campaña actualizada', description: payload.name, variant: 'success' });
      } else {
        await createFeedbackCampaign(payload);
        toast({ title: 'Campaña creada', description: payload.name, variant: 'success' });
      }
      resetForm();
      load();
    } catch (err) {
      toast({
        title: 'No se pudo guardar',
        description: err instanceof Error ? err.message : 'Error',
        variant: 'default',
      });
    } finally {
      setBusy(false);
    }
  };

  const removeCampaign = async (c: FeedbackCampaign) => {
    if (!window.confirm(`¿Borrar la campaña "${c.name}"? Sus respuestas se conservan.`)) return;
    setBusy(true);
    try {
      await deleteFeedbackCampaign(c.id);
      toast({ title: 'Campaña borrada', description: c.name, variant: 'success' });
      load();
    } catch (err) {
      toast({
        title: 'No se pudo borrar',
        description: err instanceof Error ? err.message : 'Error',
        variant: 'default',
      });
    } finally {
      setBusy(false);
    }
  };

  const columns: DataTableColumn<FeedbackCampaign>[] = [
    {
      key: 'name',
      header: 'Campaña',
      accessor: (c) => c.name,
      sortable: true,
      width: '32%',
    },
    {
      key: 'dates',
      header: 'Fechas',
      accessor: (c) => `${formatDate(c.startDate)} – ${formatDate(c.endDate)}`,
      sortable: true,
    },
    {
      key: 'status',
      header: 'Estado',
      accessor: (c) => {
        const s = STATUS_BADGE[campaignStatus(c)];
        return <Badge variant={s.variant}>{s.label}</Badge>;
      },
      sortable: true,
    },
    {
      key: 'responses',
      header: 'Respuestas',
      accessor: (c) => c.responseCount ?? 0,
      sortable: true,
      align: 'center',
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      accessor: (c) => (
        <div className={styles.actions} onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              startEdit(c);
            }}
          >
            Editar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              void removeCampaign(c);
            }}
          >
            Borrar
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>Feedback</h1>
          <p className={styles.subtitle}>
            Campañas del cuestionario. Entra en una para ver sus resultados.
          </p>
        </div>
        <Button leftIcon={<Plus />} onClick={startCreate}>
          Nueva campaña
        </Button>
      </div>

      {formOpen && (
        <Card className={styles.formCard}>
          <h2 className={styles.formCard__title}>{editingId ? 'Editar campaña' : 'Nueva campaña'}</h2>
          <div className={styles.form}>
            <Input
              placeholder="Nombre (ej. Agosto 2026)"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              fullWidth
            />
            <div className={styles.form__dates}>
              <label className={styles.form__field}>
                <span>Inicio</span>
                <Input type="date" value={formStart} onChange={(e) => setFormStart(e.target.value)} />
              </label>
              <label className={styles.form__field}>
                <span>Fin</span>
                <Input type="date" value={formEnd} onChange={(e) => setFormEnd(e.target.value)} />
              </label>
            </div>
            <div className={styles.form__actions}>
              <Button size="sm" isLoading={busy} onClick={() => void submitCampaign()}>
                {editingId ? 'Guardar' : 'Crear'}
              </Button>
              <Button variant="ghost" size="sm" onClick={resetForm}>
                Cancelar
              </Button>
            </div>
          </div>
        </Card>
      )}

      {error ? (
        <div className={styles.center}>
          <p className={styles.error}>{error}</p>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={campaigns ?? []}
          onRowClick={(c) => navigate(`${ROUTES.BACKOFFICE.FEEDBACK}/${c.id}`)}
          isLoading={isLoading}
          emptyMessage="No hay campañas. Crea una para empezar a recoger feedback."
        />
      )}
    </div>
  );
}

export default FeedbackListPage;
